import { useEffect, useMemo } from "react";
import { CommonActions } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { CareStackParamList } from "../types";
import { useSmartBack } from "../hooks/useSmartBack";
import { getMetricsByKey } from "../data/metricFactsheets";
import {
  getDataPointForMarkerName,
  getDataPointsForTest,
  getTestsForDataPoint,
} from "@src/packages/content";
import { useLocale } from "@src/packages/i18n";

type Props = NativeStackScreenProps<CareStackParamList, "MetricDetail">;

/**
 * The `MetricDetail` route, kept for the links that still name a metric.
 *
 * Every metric factsheet, in both locales, resolves to a canonical test, so
 * the route hands over to that test's detail and renders nothing itself. The
 * metric sheet it used to fall back to could not be reached and is gone; if a
 * metric ever stops resolving, the route steps back rather than showing an
 * empty screen.
 */
export default function MetricDetailPreviewScreen({
  navigation,
  route,
}: Props) {
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const metric = useMemo(() => {
    const byKey = getMetricsByKey(locale);
    const key = route.params?.metricKey ?? "estradiol";
    return byKey[key] ?? byKey.estradiol ?? null;
  }, [route.params?.metricKey, locale]);
  const canonicalTestId = useMemo(() => {
    if (!metric) return null;
    const dataPoint = getDataPointForMarkerName(metric.label);
    if (!dataPoint) return null;
    const tests = getTestsForDataPoint(dataPoint.id);
    return (
      tests.find((test) => getDataPointsForTest(test.id).length === 1)?.id ??
      tests[0]?.id ??
      null
    );
  }, [metric]);

  useEffect(() => {
    if (!canonicalTestId) {
      handleBack();
      return;
    }
    const localState = navigation.getState();
    const hasOriginRoute = localState.index > 0;
    if (hasOriginRoute) {
      navigation.goBack();
    }
    navigation.getParent()?.dispatch(
      CommonActions.navigate({
        name: "Access",
        params: {
          screen: "TestDetail",
          params: {
            testId: String(canonicalTestId),
            ...(hasOriginRoute ? { returnTab: "Care" as const } : {}),
          },
        },
      }),
    );
  }, [canonicalTestId, handleBack, navigation]);

  return null;
}
